import { ref, type ToRefs } from "vue";

import { fetchWithCache } from "~/utils/fetch";

import type { PBCollection } from "~/types/pb/collections";
import { Sub } from "~/types/subscriptions";

import Subscription, { type SubscriptionConfig } from "~/classes/Subscription";

export interface ISubscriptionDriveSquad {
	url: string;
	visibleUrl: string;
	quotaUser: number;
	quotaStorage: number;
}

export type SubDriveSquad = Subscription & ISubscriptionDriveSquad;

/**
 * Represents a Squad subscription.
 */
export default class SubscriptionDriveSquad
	extends Subscription
	implements ToRefs<ISubscriptionDriveSquad> {
	public url = ref<string>("");

	public visibleUrl = ref<string>("");

	public quotaUser = ref<number>(0);

	public quotaStorage = ref<number>(0);

	/**
	 * Creates a new SubscriptionDriveSquad instance.
	 *
	 * @param {SubscriptionConfig} config - The configuration object for the subscription
	 */
	public constructor (config: SubscriptionConfig) {
		super(config);

		this.fetchSquadData()
			.then(() => __subscription().setLoading(Sub.Type.DriveSquad, false))
			.catch((error: any) => {
				__subscription().setLoading(Sub.Type.DriveSquad, false);
				console.error(error);
			});
	}

	/**
	 * Fetches Squad data from PocketBase.
	 *
	 * @returns {Promise<void>} A Promise that resolves once the Squad data has been fetched.
	 */
	private async fetchSquadData (): Promise<void> {
		try {
			const pbInfos = await PB.i
				.collection<PBCollection.AboDriveSquad>("drive_squadSubscriptions")
				.getFullList({
					requestKey: "get_all_drive_squad_infos",
					fetch: fetchWithCache
				});

			if (pbInfos.length === 0) {
				console.warn("No drive squad infos found in PB");
				return;
			}

			const squadInfo = pbInfos.find(info => info.subscriptionId === this.subscriptionItem.id);
			if (!squadInfo) {
				console.warn("No drive squad infos found in PB for this subscription", this.subscriptionItem.id);
				return;
			}

			console.info("🟢 Drive Squad PB data:", squadInfo);

			this.url.value = squadInfo.url;
			this.setVisibleUrl();
			this.quotaUser.value = squadInfo.quotaUser;
			this.quotaStorage.value = squadInfo.quotaStorage;
		} catch (error) {
			console.error("Error fetching drive squad data:", error);
		}
	}

	/**
	 * Set the visible URL.
	 *
	 * @param url - The URL to set as visible (optional)
	 */
	private setVisibleUrl (url?: string): void {
		try {
			const urlToUse = url || this.url.value;
			const urlObject = new URL(urlToUse);
			this.visibleUrl.value = urlObject.hostname;
		} catch (error) {
			console.error("Invalid URL format: ", error);
			this.visibleUrl.value = "";
		}
	}
}
